import { create } from 'zustand';
import { isNative } from './platform';
import { PORTAL } from './portal';

/** Chrome's install prompt event (not in the DOM typings). */
interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const env = import.meta.env;
/** Store listings, once the apps are published (empty until then). */
export const PLAY_STORE_URL = (env.VITE_PLAY_STORE_URL as string | undefined) || '';
export const APP_STORE_URL = (env.VITE_APP_STORE_URL as string | undefined) || '';

const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
// iPadOS reports itself as a Mac, but with touch.
const isIos = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && typeof navigator !== 'undefined' && navigator.maxTouchPoints > 1);
const isAndroid = /Android/i.test(ua);
const standalone = () =>
  typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true);

/**
 * How this device can get the app:
 * - store:  a store listing exists for this platform (Play Store on Android, App Store on iOS)
 * - prompt: the browser offers to install the web app (Chrome / Edge / Samsung Internet)
 * - ios:    iPhone/iPad Safari, which installs only by hand (Share, then Add to Home Screen)
 * - null:   nothing to offer (already the app, already installed, a game portal, or no way to install)
 */
export type InstallWay = 'store' | 'prompt' | 'ios' | null;

interface InstallState {
  prompt: InstallPromptEvent | null;
  installed: boolean;
}

const useInstallState = create<InstallState>(() => ({ prompt: null, installed: false }));

/** Call once at startup: the browser fires its install prompt early, often before React renders. */
export function captureInstallPrompt(): void {
  if (isNative || PORTAL) return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    useInstallState.setState({ prompt: e as InstallPromptEvent });
  });
  window.addEventListener('appinstalled', () => useInstallState.setState({ prompt: null, installed: true }));
}

export function storeUrl(): string {
  return isIos ? APP_STORE_URL : isAndroid ? PLAY_STORE_URL : '';
}

export function useInstall(): { way: InstallWay; install: () => Promise<void> } {
  const { prompt, installed } = useInstallState();
  let way: InstallWay = null;
  if (!isNative && !PORTAL && !installed && !standalone()) {
    if (storeUrl()) way = 'store';
    else if (prompt) way = 'prompt';
    else if (isIos) way = 'ios';
  }
  const install = async () => {
    if (way === 'store') window.open(storeUrl(), '_blank', 'noopener');
    else if (way === 'prompt' && prompt) {
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;
      // A prompt can be shown only once; after "not now" the browser may offer it again later.
      useInstallState.setState({ prompt: null, installed: outcome === 'accepted' });
    }
  };
  return { way, install };
}
